/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/*
 * ============================================================
 * Auction Plus + Ivory/Focal AJAX Collection Fix
 *
 * Fixes missing Auction Plus "PLACE BID" buttons after:
 * - Collection pagination
 * - Filtering
 * - Sorting
 *
 * Ivory/Focal replaces collection products with AJAX.
 * Auction Plus does not automatically refresh its auction
 * configuration after that replacement.
 * ============================================================
 */

(function () {
  'use strict';
  if (!window.location.pathname.startsWith('/collections/')) {
    return;
  }
  let auctionRefreshTimer = null;
  let auctionRefreshRunning = false;
  let lastCollectionSignature = '';
  let suppressUntil = 0;

  /*
   * Get the products currently displayed in the collection.
   * We use this to determine whether Ivory actually changed
   * the product grid.
   */
  function getCollectionSignature() {
    const container =
      document.querySelector('#facet-main') ||
      document.querySelector('[data-facet-main]') ||
      document;

    const handles = [
      ...container.querySelectorAll('a[href*="/products/"]')
    ]
      .map(function (link) {
        const parts = link.href.split('/products/');

        if (!parts[1]) {
          return null;
        }

        return parts[1].split('?')[0];
      })
      .filter(Boolean)
      .filter(function (value, index, array) {
        return array.indexOf(value) === index;
      });

    return location.href + '|' + handles.join('|');
  }


  /*
   * Extract the auctions: [...] array from the fresh
   * Auction Plus initialization script.
   */
  function extractAuctionArray(source) {
    const initPos = source.indexOf(
      'auctionPlusSetup.init('
    );

    if (initPos === -1) {
      throw new Error(
        'auctionPlusSetup.init() not found'
      );
    }

    const markerPos = source.indexOf(
      'auctions:',
      initPos
    );

    if (markerPos === -1) {
      throw new Error(
        'Auction Plus auctions property not found'
      );
    }

    const start = source.indexOf(
      '[',
      markerPos
    );

    if (start === -1) {
      throw new Error(
        'Auction Plus auctions array not found'
      );
    }

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < source.length; i++) {
      const char = source[i];

      if (inString) {
        if (escaped) {
          escaped = false;
        }
        else if (char === '\\') {
          escaped = true;
        }
        else if (char === '"') {
          inString = false;
        }

        continue;
      }

      if (char === '"') {
        inString = true;
        continue;
      }

      if (char === '[') {
        depth++;
      }
      else if (char === ']') {
        depth--;

        if (depth === 0) {
          let jsonText = source.substring(
            start,
            i + 1
          );

          /*
           * Remove JS-style trailing commas because
           * JSON.parse() does not accept them.
           */
          jsonText = jsonText.replace(
            /,\s*([}\]])/g,
            '$1'
          );

          return JSON.parse(jsonText);
        }
      }
    }

    throw new Error(
      'Could not find end of Auction Plus auctions array'
    );
  }


  /*
   * Fetch the current collection URL and retrieve
   * the fresh Auction Plus auction configuration.
   */
  async function refreshAuctionPlusCollection() {
    if (auctionRefreshRunning) {
      return;
    }

    if (
      !window.auctionPlusSetup ||
      !window.auctionApp
    ) {
      console.log(
        '[Auction Plus Fix] Auction Plus not ready yet'
      );

      return;
    }

    auctionRefreshRunning = true;

    try {
      console.log(
        '[Auction Plus Fix] Refreshing:',
        location.href
      );

      const response = await fetch(
        location.href,
        {
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        throw new Error(
          'Collection fetch failed: ' +
          response.status
        );
      }

      const html = await response.text();

      const doc = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const script = [
        ...doc.querySelectorAll('script')
      ].find(function (scriptElement) {
        return (
          scriptElement.textContent.includes(
            'auctionPlusSetup.init('
          ) &&
          scriptElement.textContent.includes(
            '"partner_product_handle"'
          )
        );
      });

      if (!script) {
        throw new Error(
          'Auction Plus configuration script not found'
        );
      }

      const freshAuctions =
        extractAuctionArray(
          script.textContent
        );

      console.log(
        '[Auction Plus Fix] Fresh auctions:',
        freshAuctions.length
      );

      /*
       * Replace stale collection data.
       */
      window.auctionPlusSetup.auctions =
        freshAuctions;

      if (window.auctionApp.config) {
        window.auctionApp.config.auctions =
          freshAuctions;
      }

      /*
       * Update Auction Plus internal auction storage.
       */
      if (
        typeof window.auctionApp.updateAuctions ===
        'function'
      ) {
        window.auctionApp.updateAuctions(
          freshAuctions
        );
      }

      /*
       * Sync current auction state.
       */
      if (
        typeof window.auctionApp.syncAuctions ===
        'function'
      ) {
        window.auctionApp.syncAuctions();
      }

      /*
       * Re-render Auction Plus collection components.
       * This is what restores PLACE BID.
       */
      if (
        typeof window.auctionPlusSetup
          .handleCollectionPage === 'function'
      ) {
        window.auctionPlusSetup
          .handleCollectionPage();
      }

      /*
       * Prevent our own Auction Plus DOM changes from
       * triggering another refresh immediately.
       */
      suppressUntil = Date.now() + 1500;

      lastCollectionSignature =
        getCollectionSignature();

      console.log(
        '[Auction Plus Fix] Collection refreshed'
      );
    }
    catch (error) {
      console.error(
        '[Auction Plus Fix]',
        error
      );
    }
    finally {
      auctionRefreshRunning = false;
    }
  }


  /*
   * Wait briefly because Ivory can make several DOM
   * changes during one pagination/filter operation.
   */
  function scheduleAuctionRefresh() {
    clearTimeout(auctionRefreshTimer);

    auctionRefreshTimer = setTimeout(
      function () {
        if (Date.now() < suppressUntil) {
          return;
        }

        const currentSignature =
          getCollectionSignature();

        /*
         * Nothing meaningful changed.
         */
        if (
          currentSignature ===
          lastCollectionSignature
        ) {
          return;
        }

        refreshAuctionPlusCollection();
      },
      500
    );
  }


  /*
   * Ivory may replace #facet-main itself.
   * Therefore observe document.body instead of
   * attaching directly to the old #facet-main.
   */
  function startAuctionPlusWatcher() {
    lastCollectionSignature =
      getCollectionSignature();

    const observer = new MutationObserver(
      function () {
        if (Date.now() < suppressUntil) {
          return;
        }

        scheduleAuctionRefresh();
      }
    );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

    /*
     * Also watch browser history changes.
     * Ivory changes ?page=, filters, etc. without
     * doing a normal page load.
     */
    window.addEventListener(
      'popstate',
      scheduleAuctionRefresh
    );

    console.log(
      '[Auction Plus Fix] Ivory AJAX watcher active'
    );
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startAuctionPlusWatcher
    );
  }
  else {
    startAuctionPlusWatcher();
  }

})();

